<script setup lang="ts">
const props = defineProps<{
  currentPage: number;
  surroundingPagesDisplayed: number;
  totalPages: number;
}>();

defineEmits(['pageUpdated']);

const displayedPageNumbers = computed(() => {
  const start: number = Math.max(1, props.currentPage - props.surroundingPagesDisplayed);
  const end: number = Math.min(props.totalPages, props.currentPage + props.surroundingPagesDisplayed);
  const pages: Array<number> = [];

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

watch(
  () => props.currentPage,
  () => {
    navigateTo({
      query: {
        page: props.currentPage,
      },
      force: true,
    });
  },
);

const { query } = useRoute();
if (Number(query.page) > props.totalPages) {
  navigateTo({
    query: {
      page: props.totalPages,
    },
    force: true,
  });
}
if (Number(query.page) < 1) {
  navigateTo({
    query: {
      page: 1,
    },
    force: true,
  });
}
</script>

<template>
  <section>
    <ul
      :aria-label="$t('Aria.Global.Pagination')"
      class="flex gap-3"
    >
      <li>
        <PartialsPaginationButton
          :aria-label="$t('Aria.Global.Pagination.FirstPage')"
          class="flex items-center justify-center"
          :disabled="currentPage <= 1"
          @click="$emit('pageUpdated', 1)"
        >
          <BaseIcon
            name="chevron-double-right"
            prefix="arrows"
            fill-color="currentColor"
            class="w-4 rotate-180"
          />
        </PartialsPaginationButton>
      </li>
      <li>
        <PartialsPaginationButton
          :aria-label="$t('Aria.Global.Pagination.PreviousPage')"
          :disabled="currentPage === 1"
          @click="$emit('pageUpdated', currentPage - 1)"
        >
          <BaseIcon
            name="chevron-right"
            prefix="arrows"
            stroke-color="currentColor"
            class="rotate-180 text-icon"
          />
        </PartialsPaginationButton>
      </li>
      <li
        v-for="page in displayedPageNumbers"
        :key="`pageButton-${page}`"
      >
        <PartialsPaginationButton
          :page-index="page"
          :aria-label="$t('Aria.Global.Pagination.SpecificPage', page)"
          :active="currentPage === page"
          class="px-4 font-bold"
          @click="$emit('pageUpdated', page)"
        >
          {{ page }}
        </PartialsPaginationButton>
      </li>
      <li>
        <PartialsPaginationButton
          :aria-label="$t('Aria.Global.Pagination.NextPage')"
          :disabled="currentPage === totalPages"
          @click="$emit('pageUpdated', currentPage + 1)"
        >
          <BaseIcon
            name="chevron-right"
            prefix="arrows"
            stroke-color="currentColor"
            class="text-icon"
          />
        </PartialsPaginationButton>
      </li>
      <li>
        <PartialsPaginationButton
          :aria-label="$t('Aria.Global.Pagination.LastPage')"
          :disabled="currentPage >= totalPages"
          class="flex items-center justify-center"
          @click="$emit('pageUpdated', totalPages)"
        >
          <BaseIcon
            name="chevron-double-right"
            fill-color="currentColor"
            prefix="arrows"
            class="w-4"
          />
        </PartialsPaginationButton>
      </li>
    </ul>
  </section>
</template>
